import { lt, showSuccessAlert, useDialogAction } from '@mews/framework';
import { useRepositoryMutation } from '@mews-commander/data-access';
import { Button, Dialog, Stack, Typography } from '@optimus-web/core';

interface Params {
    centralProductIds: string[];
    propertyIdsLength: number;
    onSuccess?: () => void;
}
export const useDeleteCentralProductDialog = ({ centralProductIds, propertyIdsLength, onSuccess }: Params) => {
    const { states, handlers } = useDialogAction(async () => Promise.resolve());

    const centralProductsCount = centralProductIds.length;

    const { mutateAsync, isPending } = useRepositoryMutation('portfolioCentralProducts', 'delete', {
        invalidateQueries: {
            portfolioCentralProducts: ['getOverviewEntries'],
            portfolioProducts: ['getOverviewEntries'],
        },
        onSuccess: (_, variables) => {
            showSuccessAlert({
                title:
                    propertyIdsLength === 0 || variables.unlink
                        ? lt('CentralProductDeleteSuccessMessage', { centralProductsCount })
                        : lt('CentralProductDeleteAllSuccessMessage'),
                description: variables.unlink ? lt('CentralProductDeleteSuccessMessageDescriptionUnlink') : undefined,
            });
            onSuccess?.();
        },
    });

    const confirmationDialog = (
        <Dialog
            title={lt('CentralProductDeleteDialogTitle', {
                centralProductsCount,
            })}
            active={states.isDialogOpened}
            loading={isPending || states.isProcessing}
            onClose={handlers.handleCancel}
            modal
            footer={{
                primary: (
                    <>
                        {propertyIdsLength > 0 ? (
                            <Button
                                variant="secondary"
                                onClick={async () => {
                                    await mutateAsync({ centralProductIds, unlink: true });
                                    await handlers.handleConfirm();
                                }}
                                type="submit"
                                size="medium"
                            >
                                {lt('CentralProductDelete', { centralProductsCount })}
                            </Button>
                        ) : null}
                        <Button
                            variant="destructive"
                            onClick={async () => {
                                await mutateAsync({ centralProductIds, unlink: false });
                                await handlers.handleConfirm();
                            }}
                            type="submit"
                            size="medium"
                        >
                            {propertyIdsLength > 0 ? lt('CentralProductDeleteAll') : lt('Delete')}
                        </Button>
                    </>
                ),
                secondary: (
                    <Button variant="tertiary" onClick={handlers.handleCancel} type="button" size="medium">
                        {lt('GoBack')}
                    </Button>
                ),
            }}
        >
            <Stack vertical spacing="200">
                <Typography textStyle="bodyMedium" as="div">
                    {propertyIdsLength === 0
                        ? lt('CentralProductDeleteDialogDescriptionUnassigned', {
                              centralProductsCount,
                          })
                        : lt('CentralProductDeleteDialogDescriptionAssigned', {
                              centralProductsCount,
                              propertyCount: propertyIdsLength,
                          })}
                </Typography>
            </Stack>
        </Dialog>
    );

    return { confirmationDialog, openConfirmationDialog: handlers.handleOpen };
};
